'use client'

import React from 'react'

import { type LucideIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Types
// ============================================================================

interface SidebarWidgetV2Props {
  children: React.ReactNode
  className?: string
}

interface SidebarWidgetHeaderProps {
  children: React.ReactNode
  icon?: LucideIcon
  className?: string
}

interface SidebarWidgetContentProps {
  children: React.ReactNode
  className?: string
}

interface SidebarWidgetFieldProps {
  label: string
  children: React.ReactNode
  /**
   * Override classes applied to the value row. Merged with the default
   * `text-sm font-medium text-primary-900 dark:text-primary-100`.
   * Use this to switch to a flex layout, apply `font-mono`, etc.
   */
  valueClassName?: string
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * SidebarWidgetV2.Header - Widget header with optional icon
 */
const Header: React.FC<SidebarWidgetHeaderProps> = ({
  children,
  icon: Icon,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50',
        className,
      )}
    >
      <h3 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {children}
      </h3>
    </div>
  )
}
Header.displayName = 'SidebarWidgetV2.Header'

/**
 * SidebarWidgetV2.Content - Widget content area
 */
const Content: React.FC<SidebarWidgetContentProps> = ({
  children,
  className,
}) => {
  return <div className={twMerge('space-y-4 p-4', className)}>{children}</div>
}
Content.displayName = 'SidebarWidgetV2.Content'

/**
 * SidebarWidgetV2.Field - Label + value pair for sidebar widget content
 *
 * Renders a small uppercase label above a value row. Compose inside
 * `SidebarWidgetV2.Content` for consistent spacing.
 *
 * @example
 * ```tsx
 * <SidebarWidgetV2.Field label="Request Type">
 *   {request.type_label}
 * </SidebarWidgetV2.Field>
 * <SidebarWidgetV2.Field label="Reference #" valueClassName="font-mono">
 *   {request.reference_number}
 * </SidebarWidgetV2.Field>
 * ```
 */
const Field: React.FC<SidebarWidgetFieldProps> = ({
  label,
  children,
  valueClassName,
}) => {
  return (
    <div>
      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
        {label}
      </span>
      <div
        className={twMerge(
          'text-sm font-medium text-primary-900 dark:text-primary-100',
          valueClassName,
        )}
      >
        {children}
      </div>
    </div>
  )
}
Field.displayName = 'SidebarWidgetV2.Field'

// ============================================================================
// Main Component
// ============================================================================

/**
 * SidebarWidgetV2 - Card container for sidebar content
 *
 * Compound component for sidebar widgets with a header and content area.
 *
 * @example
 * ```tsx
 * <SidebarWidgetV2>
 *   <SidebarWidgetV2.Header icon={Clock}>
 *     Key Dates
 *   </SidebarWidgetV2.Header>
 *   <SidebarWidgetV2.Content>
 *     <TimelineV2>
 *       ...
 *     </TimelineV2>
 *   </SidebarWidgetV2.Content>
 * </SidebarWidgetV2>
 * ```
 */
const SidebarWidgetV2: React.FC<SidebarWidgetV2Props> & {
  Header: typeof Header
  Content: typeof Content
  Field: typeof Field
} = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
        className,
      )}
    >
      {children}
    </div>
  )
}

SidebarWidgetV2.displayName = 'SidebarWidgetV2'
SidebarWidgetV2.Header = Header
SidebarWidgetV2.Content = Content
SidebarWidgetV2.Field = Field

// ============================================================================
// Skeleton Sub-Component
// ============================================================================

interface SidebarWidgetSkeletonProps {
  /** Header title width */
  headerWidth?: 'sm' | 'md' | 'lg'
  children?: React.ReactNode
  className?: string
}

const headerWidthClasses = {
  sm: 'w-24',
  md: 'w-32',
  lg: 'w-40',
}

/**
 * SidebarWidgetV2.Skeleton - Loading skeleton for sidebar widgets
 *
 * Pass children for custom content skeletons, or use default placeholder.
 *
 * @example
 * ```tsx
 * <SidebarWidgetV2.Skeleton headerWidth="lg">
 *   <TimelineV2.Skeleton nodeCount={4} />
 * </SidebarWidgetV2.Skeleton>
 * ```
 */
const SidebarWidgetSkeleton: React.FC<SidebarWidgetSkeletonProps> = ({
  headerWidth = 'md',
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
        className,
      )}
    >
      {/* Header skeleton */}
      <div className="border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-3.5 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          <div
            className={twMerge(
              'h-3 animate-pulse rounded bg-primary-200 dark:bg-primary-700',
              headerWidthClasses[headerWidth],
            )}
          />
        </div>
      </div>

      {/* Content skeleton */}
      <div className="space-y-4 p-4">
        {children || (
          <>
            {/* Default placeholder content */}
            <div className="space-y-2">
              <div className="h-3 w-20 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
              <div className="h-4 w-32 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            </div>
            <div className="space-y-2">
              <div className="h-3 w-16 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
              <div className="h-4 w-28 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

SidebarWidgetSkeleton.displayName = 'SidebarWidgetV2.Skeleton'

// Attach skeleton to SidebarWidgetV2
const SidebarWidgetV2WithSkeleton =
  SidebarWidgetV2 as typeof SidebarWidgetV2 & {
    Skeleton: typeof SidebarWidgetSkeleton
  }
SidebarWidgetV2WithSkeleton.Skeleton = SidebarWidgetSkeleton

export { SidebarWidgetV2WithSkeleton as SidebarWidgetV2 }
export type {
  SidebarWidgetV2Props,
  SidebarWidgetHeaderProps,
  SidebarWidgetContentProps,
  SidebarWidgetFieldProps,
  SidebarWidgetSkeletonProps,
}
